import React, { useCallback, useContext, useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { useHistory, useParams } from "react-router-dom";
import { observer } from "mobx-react";
import { Step } from "src/screens/process/step/Step";
import { Location } from "src/api/Notification";
import { SubmitHandler, useForm } from "react-hook-form";
import { runInAction } from "mobx";
import {
    GetCitiesByZip,
    GetCitiesByName,
    GetCities,
    GetStreetsInCity,
    GetStreetNumbersInStreet
} from "src/utils/Location";
import debounce from "lodash/debounce";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "src/screens/process/realestate/Realestate.css";

export enum ERROR_TYPE {
    NONE = 0,
    ZIP = 1,
    CITY = 2,
    STREET = 3,
    STREETNUMBER = 4,
    FLOOR = 5
}

enum SEARCH_STATE {
    NONE = 0,
    ZIP = 1,
    CITY = 2,
    STREET = 3,
    STREETNUMBER = 4
}

enum LIST_TYPE {
    ZIP = "zip",
    CITY = "city",
    STREET = "street",
    STREETNUMBER = "streetnumber"
}

const RealestateBase = () => {
    const { t } = useTranslation();

    const { easyContactStore, uiStore } = useContext(RootStoreContext);
    const { nextStep } = easyContactStore.stepNavigation;

    const history = useHistory();
    const { processId } = useParams<{ processId: string; stepId: string }>();

    const realestate_schema = yup.object().shape({
        zip: yup.string().required(t("form.required_field")),
        city: yup.string().required(t("form.required_field")),
        street: yup.string().required(t("form.required_field")),
        number: yup.string().required(t("form.required_field")),
        floor: yup.string()
    });

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors }
    } = useForm<Location>({
        mode: "onBlur",
        resolver: yupResolver(realestate_schema),
        defaultValues: {
            zip: easyContactStore.notification.location?.zip || "",
            city: easyContactStore.notification.location?.city || "",
            street: easyContactStore.notification.location?.street || "",
            number: easyContactStore.notification.location?.number || "",
            floor: easyContactStore.notification.location?.floor || ""
        }
    });

    const setFocus = useCallback((inputElementId: string) => {
        const inputElement: HTMLInputElement | null = document?.getElementById(inputElementId) as HTMLInputElement;

        if (inputElement) {
            var tempEl = document.createElement("input");
            tempEl.style.position = "absolute";
            tempEl.style.top = inputElement.offsetTop + 7 + "px";
            tempEl.style.left = inputElement.offsetLeft + "px";
            tempEl.style.height = "0";
            tempEl.style.opacity = "0";

            document.body.appendChild(tempEl);
            tempEl.focus();

            setTimeout(function () {
                inputElement.focus();
                inputElement.click();

                document.body.removeChild(tempEl);
            }, 100);
        }
    }, []);

    useEffect(() => {
        setFocus("zip");
    }, [setFocus]);

    const [zipItems, setZipItems] = useState<string[]>([]);
    const [nameItems, setNameItems] = useState<string[]>([]);
    const [streetItems, setStreetItems] = useState<{ id: string; street: string }[]>([]);
    const [streetNumberItems, setStreetNumberItems] = useState<string[]>([]);
    const [numbersOfSelectedStreet, setNumbersOfSelectedStreet] = useState<string[]>([]);

    const [zipWasSelected, setZipWasSelected] = useState<boolean>(
        easyContactStore.notification.location?.zip ? true : false
    );
    const [cityWasSelected, setCityWasSelected] = useState<boolean>(
        easyContactStore.notification.location?.city ? true : false
    );
    const [streetWasSelected, setStreetWasSelected] = useState<boolean>(
        easyContactStore.notification.location?.street ? true : false
    );
    const [streetNumberWasSelected, setStreetNumberWasSelected] = useState<boolean>(
        easyContactStore.notification.location?.number ? true : false
    );

    const [searchType, setSearchType] = useState<SEARCH_STATE>(SEARCH_STATE.NONE);

    const [error, setError] = useState<ERROR_TYPE>(ERROR_TYPE.NONE);

    const [activeListItemIndex, setActiveListItemIndex] = useState<number>(-1);

    useEffect(() => {
        history.push({ search: "" });
    }, [history]);

    const onSubmit: SubmitHandler<Location> = useCallback(
        (data) => {
            runInAction(() => {
                easyContactStore.setLocation(data);
            });

            if (nextStep) {
                history.push(`/process/${processId}/${nextStep.type}/${nextStep.id}`);
            }
        },
        [history, easyContactStore, nextStep, processId]
    );

    const closeAllLists = useCallback(() => {
        setZipItems([]);
        setNameItems([]);
        setStreetItems([]);
        setStreetNumberItems([]);
    }, []);

    const onChangeInputCityZip = debounce(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputZip = e.target.value.trim();

        setError(ERROR_TYPE.NONE);

        setZipWasSelected(false);
        setStreetWasSelected(false);
        setStreetNumberWasSelected(false);

        setValue("street", "");
        setValue("number", "");

        setSearchType(SEARCH_STATE.ZIP);

        if (inputZip.length > 1) {
            const cities = await GetCitiesByZip(inputZip);

            setZipItems(cities.map((city: any) => `${city.zipcode} ${city.city}`));

            if (cities.length === 0) {
                setError(ERROR_TYPE.ZIP);
            }
        } else {
            setZipItems([]);
            setNameItems([]);
        }

        setSearchType(SEARCH_STATE.NONE);
    }, 800);

    const onChangeInputCityName = debounce(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputName = e.target.value.trim();

        if (inputName !== watch("city") || !streetWasSelected) {
            closeAllLists();
            setError(ERROR_TYPE.NONE);

            setCityWasSelected(false);
            setStreetWasSelected(false);
            setStreetNumberWasSelected(false);

            setValue("street", "");
            setValue("number", "");

            setSearchType(SEARCH_STATE.CITY);

            if (inputName.length > 1 && zipItems.length === 0) {
                const cities = await GetCitiesByName(inputName);

                setNameItems(cities.map((city: any) => `${city.zipcode} ${city.city}`));

                if (cities.length === 0) {
                    setError(ERROR_TYPE.CITY);
                }
            } else {
                setZipItems([]);
                setNameItems([]);
            }

            setSearchType(SEARCH_STATE.NONE);
        }
    }, 800);

    const onChangeInputStreet = debounce(async (e: React.ChangeEvent<HTMLInputElement>) => {
        setError(ERROR_TYPE.NONE);
        closeAllLists();

        setStreetWasSelected(false);
        setStreetNumberWasSelected(false);
        setValue("number", "");

        setSearchType(SEARCH_STATE.STREET);

        const inputStreet = e.target.value.trim();

        if (inputStreet.length > 1) {
            const streets = await GetStreetsInCity({ zip: watch("zip"), streetName: inputStreet });

            setStreetItems(streets);

            if (streets.length === 0) {
                setError(ERROR_TYPE.STREET);
            }
        } else {
            setStreetItems([]);
        }

        setSearchType(SEARCH_STATE.NONE);
    }, 800);

    const onChangeInputStreetNumber = debounce(async (e: React.ChangeEvent<HTMLInputElement>) => {
        setError(ERROR_TYPE.NONE);

        setSearchType(SEARCH_STATE.STREETNUMBER);

        const inputNumber = e.target.value.toLowerCase().trim();
        setStreetNumbers(easyContactStore.streetId);

        if (inputNumber && numbersOfSelectedStreet && numbersOfSelectedStreet.length > 0) {
            const foundNumbers = numbersOfSelectedStreet.filter((number: string) => number.includes(inputNumber));

            if (foundNumbers.length > 0) {
                setStreetNumberItems(foundNumbers);
            } else {
                setError(ERROR_TYPE.STREETNUMBER);
                setStreetNumberItems([]);
                setStreetNumberWasSelected(false);
            }
        } else {
            setError(ERROR_TYPE.NONE);
            setStreetNumberItems([]);
            setStreetNumberWasSelected(false);
        }

        setSearchType(SEARCH_STATE.NONE);
    }, 800);

    const onClickInputCityZip = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            closeAllLists();
            setActiveListItemIndex(-1);

            const inputZip = e.target.value.trim();
            if (inputZip.length > 1 && !zipWasSelected) {
                const cities = await GetCitiesByZip(inputZip);

                setZipItems(cities.map((city: any) => `${city.zipcode} ${city.city}`));

                if (cities.length === 0) {
                    setError(ERROR_TYPE.ZIP);
                }
            } else {
                setZipItems([]);
                setNameItems([]);
            }
        },
        [closeAllLists, zipWasSelected]
    );

    const onClickInputCityName = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            closeAllLists();
            setActiveListItemIndex(-1);

            const inputCity = e.target.value.trim();
            if (inputCity.length > 1 && zipItems.length === 0 && !cityWasSelected) {
                const cities = await GetCitiesByName(inputCity);

                setNameItems(cities.map((city: any) => `${city.zipcode} ${city.city}`));

                if (cities.length === 0) {
                    setError(ERROR_TYPE.CITY);
                }
            } else {
                setZipItems([]);
                setNameItems([]);
            }
        },
        [closeAllLists, cityWasSelected, zipItems.length]
    );

    const onClickInputStreet = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            closeAllLists();
            setActiveListItemIndex(-1);

            const streetName = e.target.value.trim();
            if (streetName.length > 1 && !streetWasSelected) {
                const streets = await GetStreetsInCity({ zip: watch("zip"), streetName: streetName });

                setStreetItems(streets);

                if (streets.length === 0) {
                    setError(ERROR_TYPE.STREET);
                }
            } else {
                setStreetItems([]);
            }
        },
        [closeAllLists, streetWasSelected, watch]
    );

    const onClickInputStreetNumber = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            closeAllLists();
            setActiveListItemIndex(-1);

            const streetNumber = e.target.value.trim();
            if (streetNumber && numbersOfSelectedStreet && !streetNumberWasSelected) {
                const foundNumbers = numbersOfSelectedStreet.filter((number: string) => number.includes(streetNumber));

                if (foundNumbers.length === 0) {
                    setError(ERROR_TYPE.STREETNUMBER);
                } else {
                    setStreetNumberItems(foundNumbers);
                }
            } else {
                setStreetItems([]);
            }
        },
        [closeAllLists, numbersOfSelectedStreet, streetNumberWasSelected]
    );

    const getZipAndCityValues = (zipAndCity: string): { zip: string; city: string } => {
        const addressParts = zipAndCity.split(" ");

        let zip = "";
        let city = "";

        if (addressParts && addressParts.length > 0) {
            zip = addressParts[0];
            addressParts.shift();
            city = addressParts.join(" ");
        }

        return { zip: zip, city: city };
    };

    const setZipAndCityFormValues = useCallback(
        (zipAndCity: string) => {
            const { zip, city } = getZipAndCityValues(zipAndCity);

            setValue("zip", zip);
            setValue("city", city);

            setZipItems([]);
            setNameItems([]);

            setCityWasSelected(true);
            setZipWasSelected(true);

            setFocus("street");
        },
        [setValue, setFocus]
    );

    const onClickZipItem = useCallback(
        (e: any) => {
            const itemValue = e.target.innerHTML;
            setZipAndCityFormValues(itemValue);
        },
        [setZipAndCityFormValues]
    );

    const onClickNameItem = useCallback(
        (e: any) => {
            const itemValue = e.target.innerHTML;
            setZipAndCityFormValues(itemValue);
        },
        [setZipAndCityFormValues]
    );

    const setStreetFormValue = useCallback(
        (streetName: string) => {
            setValue("street", streetName);
            setStreetItems([]);
            setStreetWasSelected(true);
            setFocus("number");
        },
        [setFocus, setValue]
    );

    const setStreetNumbers = useCallback(async (streetId: string) => {
        if (streetId) {
            const streetNumbers = await GetStreetNumbersInStreet(streetId);
            setNumbersOfSelectedStreet(streetNumbers);            
        }
    }, []);

    const onClickStreetItem = useCallback(
        async (e: any) => {
            const streetName = e.target.innerHTML;
            const streetId = e.target.getAttribute("data-streetid");
            easyContactStore.setStreetId(streetId);

            setStreetFormValue(streetName);
            setStreetNumbers(streetId);
        },
        [setStreetNumbers, setStreetFormValue, easyContactStore]
    );

    const setStreetNumberFormValue = useCallback(
        (streetNumber: string) => {
            setValue("number", streetNumber);
            setStreetNumberItems([]);
            setStreetNumberWasSelected(true);
        },
        [setValue]
    );

    const onClickStreetNumberItem = useCallback(
        (e: any) => {
            const streetNumber = e.target.innerHTML;
            setStreetNumberFormValue(streetNumber);
        },
        [setStreetNumberFormValue]
    );

    const onFocusInput = useCallback(
        (e: any) => {
            closeAllLists();
            setActiveListItemIndex(-1);
        },
        [closeAllLists]
    );

    const handleKeyArrowDown = useCallback((listType: LIST_TYPE, numberOfListItems: number) => {
        setActiveListItemIndex((oldIndexValue) => {
            let newIndexValue = 0;

            if (oldIndexValue < numberOfListItems - 1) {
                newIndexValue = oldIndexValue + 1;
            } else {
                newIndexValue = numberOfListItems - 1;
            }

            const listElement = document.getElementById(`${listType}-dropdown-list`);
            const previousElement = document.getElementById(`${listType}-list-item-${oldIndexValue}`);
            const currentElement = document.getElementById(`${listType}-list-item-${newIndexValue}`);

            previousElement?.classList.remove("active");
            currentElement?.classList.add("active");

            const listHeight = listElement?.offsetHeight;
            const currentElementHeight = currentElement?.offsetHeight;
            const currentElementOffsetTop = currentElement?.offsetTop;

            if (
                typeof listHeight === "number" &&
                typeof currentElementHeight === "number" &&
                typeof currentElementOffsetTop === "number"
            ) {
                const pixelsToScroll = listHeight - currentElementHeight - currentElementOffsetTop;

                if (pixelsToScroll < 0) {
                    if (Math.abs(pixelsToScroll) > currentElementHeight) {
                        listElement?.scrollBy(0, currentElementHeight);
                    } else {
                        listElement?.scrollBy(0, Math.abs(pixelsToScroll));
                    }
                }
            }

            return newIndexValue;
        });
    }, []);

    const handleKeyArrowUp = useCallback((listType: LIST_TYPE) => {
        setActiveListItemIndex((oldIndexValue) => {
            let newIndexValue = 0;

            if (oldIndexValue !== 0) {
                newIndexValue = oldIndexValue - 1;
            }

            const listElement = document.getElementById(`${listType}-dropdown-list`);
            const previousElement = document.getElementById(`${listType}-list-item-${oldIndexValue}`);
            const currentElement = document.getElementById(`${listType}-list-item-${newIndexValue}`);

            previousElement?.classList.remove("active");
            currentElement?.classList.add("active");

            const zipListScrollTop = listElement?.scrollTop;
            const currentElementHeight = currentElement?.offsetHeight;
            const currentElementOffsetTop = currentElement?.offsetTop;

            if (
                typeof zipListScrollTop === "number" &&
                typeof currentElementHeight === "number" &&
                typeof currentElementOffsetTop === "number"
            ) {
                const pixelsToScroll = currentElementOffsetTop - zipListScrollTop;

                if (pixelsToScroll < 0) {
                    if (Math.abs(pixelsToScroll) > currentElementHeight) {
                        listElement?.scrollBy(0, -currentElementHeight);
                    } else {
                        listElement?.scrollBy(0, pixelsToScroll);
                    }
                }
            }

            return newIndexValue;
        });
    }, []);

    const onKeyDownInputCityZip = useCallback(
        async (e: any) => {
            if (e.key === "Tab") {
                closeAllLists();
                onChangeInputCityZip.cancel();
                onChangeInputCityName.cancel();

                const zip = e.target.value.trim();

                const isZipListItemSelected = zipItems.length > 0 && activeListItemIndex !== -1;

                if (isZipListItemSelected) {
                    e.preventDefault();
                    setZipAndCityFormValues(zipItems[activeListItemIndex]);
                } else if (zip.length === 4) {
                    setSearchType(SEARCH_STATE.ZIP);

                    const cities = await GetCities({ zip: zip });

                    if (cities.length > 0) {
                        const cityWithShortestName = cities.reduce(
                            (city: { city: string; zip: string }, cityB: { city: string; zip: string }) =>
                                city.city.length <= cityB.city.length ? city : cityB
                        );

                        setValue("city", cityWithShortestName.city);
                        setValue("zip", cityWithShortestName.zipcode);

                        setZipWasSelected(true);
                        setCityWasSelected(true);

                        setFocus("street");
                    }

                    setSearchType(SEARCH_STATE.NONE);
                }
            } else if (zipItems.length > 0 && e.key === "ArrowDown") {
                handleKeyArrowDown(LIST_TYPE.ZIP, zipItems.length);
            } else if (zipItems.length > 0 && e.key === "ArrowUp") {
                handleKeyArrowUp(LIST_TYPE.ZIP);
            } else if (zipItems.length > 0 && e.key === "Enter") {
                setZipAndCityFormValues(zipItems[activeListItemIndex]);
            }
        },
        [
            closeAllLists,
            onChangeInputCityName,
            onChangeInputCityZip,
            setValue,
            setFocus,
            activeListItemIndex,
            setZipAndCityFormValues,
            zipItems,
            handleKeyArrowDown,
            handleKeyArrowUp
        ]
    );

    const onKeyDownInputCityName = useCallback(
        async (e: any) => {
            if (e.key === "Tab") {
                closeAllLists();
                onChangeInputCityZip.cancel();
                onChangeInputCityName.cancel();

                const cityName = e.target.value.trim();

                const isCityNameListItemSelected = nameItems.length > 0 && activeListItemIndex !== -1;

                if (isCityNameListItemSelected) {
                    e.preventDefault();
                    setZipAndCityFormValues(nameItems[activeListItemIndex]);
                } else if (cityName.length !== 0) {
                    setSearchType(SEARCH_STATE.CITY);

                    const cities = await GetCities({ city: cityName });

                    if (cities.length > 0) {
                        const cityWithShortestName = cities.reduce(
                            (city: { city: string; zip: string }, cityB: { city: string; zip: string }) =>
                                city.city.length <= cityB.city.length ? city : cityB
                        );

                        setValue("city", cityWithShortestName.city);
                        setValue("zip", cityWithShortestName.zipcode);

                        setZipWasSelected(true);
                        setCityWasSelected(true);
                    }
                }

                setSearchType(SEARCH_STATE.NONE);
            } else if (nameItems.length > 0 && e.key === "ArrowDown") {
                handleKeyArrowDown(LIST_TYPE.CITY, nameItems.length);
            } else if (nameItems.length > 0 && e.key === "ArrowUp") {
                handleKeyArrowUp(LIST_TYPE.CITY);
            } else if (nameItems.length > 0 && e.key === "Enter") {
                setZipAndCityFormValues(nameItems[activeListItemIndex]);
            }
        },
        [
            closeAllLists,
            setValue,
            onChangeInputCityName,
            onChangeInputCityZip,
            activeListItemIndex,
            handleKeyArrowDown,
            handleKeyArrowUp,
            nameItems,
            setZipAndCityFormValues
        ]
    );

    const onKeyDownInputStreet = useCallback(
        async (e: any) => {
            if (e.key === "Tab") {
                closeAllLists();
                onChangeInputStreet.cancel();

                const streetName = e.target.value.trim();

                const isStreetListItemSelected = streetItems.length > 0 && activeListItemIndex !== -1;

                if (isStreetListItemSelected) {
                    e.preventDefault();
                    setStreetFormValue(streetItems[activeListItemIndex].street);
                    setStreetNumbers(streetItems[activeListItemIndex].id);
                } else if (streetName.length > 3) {
                    setSearchType(SEARCH_STATE.STREET);

                    const currentZipValue = watch("zip");
                    const streets = await GetStreetsInCity({ zip: currentZipValue, streetName: streetName });

                    if (streets.length === 0) {
                        setError(ERROR_TYPE.STREET);
                    }

                    const exactStreet = streets.find(
                        (street: { id: number; street: string }) =>
                            street.street.toLowerCase() === streetName.toLowerCase()
                    );

                    if (exactStreet) {
                        setStreetFormValue(exactStreet.street);
                        setStreetNumbers(exactStreet.id);
                    }

                    setSearchType(SEARCH_STATE.NONE);
                }
            } else if (streetItems.length > 0 && e.key === "ArrowDown") {
                handleKeyArrowDown(LIST_TYPE.STREET, streetItems.length);
            } else if (streetItems.length > 0 && e.key === "ArrowUp") {
                handleKeyArrowUp(LIST_TYPE.STREET);
            } else if (streetItems.length > 0 && e.key === "Enter") {
                setStreetFormValue(streetItems[activeListItemIndex].street);
                setStreetNumbers(streetItems[activeListItemIndex].id);
            }
        },
        [
            watch,
            closeAllLists,
            onChangeInputStreet,
            handleKeyArrowDown,
            handleKeyArrowUp,
            streetItems,
            activeListItemIndex,
            setStreetNumbers,
            setStreetFormValue
        ]
    );

    const onKeyDownInputStreetNumber = useCallback(
        async (e: any) => {
            if (e.key === "Tab" && e.shiftKey) {
                document?.getElementById("number")?.focus();
            } else if (e.key === "Tab") {
                closeAllLists();
                onChangeInputStreetNumber.cancel();

                const streetNumberInput = e.target.value.toLowerCase().trim();
                const isStreetNumberListItemSelected = streetNumberItems.length > 0 && activeListItemIndex !== -1;

                if (isStreetNumberListItemSelected) {
                    e.preventDefault();
                    setStreetNumberFormValue(streetNumberItems[activeListItemIndex]);
                } else if (streetNumberInput && numbersOfSelectedStreet && numbersOfSelectedStreet.length > 0) {
                    setSearchType(SEARCH_STATE.STREETNUMBER);
                    const foundNumber = numbersOfSelectedStreet.find((number: string) => number === streetNumberInput);

                    if (foundNumber) {
                        setStreetNumberFormValue(foundNumber);
                        setError(ERROR_TYPE.NONE);
                        document?.getElementById("floor")?.focus();
                    } else {
                        setError(ERROR_TYPE.STREETNUMBER);
                    }
                } else {
                    setStreetNumberItems([]);
                    setStreetNumberWasSelected(false);
                }

                document?.getElementById("floor")?.focus();

                setSearchType(SEARCH_STATE.NONE);
            } else if (streetNumberItems.length > 0 && e.key === "ArrowDown") {
                handleKeyArrowDown(LIST_TYPE.STREETNUMBER, streetNumberItems.length);
            } else if (streetNumberItems.length > 0 && e.key === "ArrowUp") {
                handleKeyArrowUp(LIST_TYPE.STREETNUMBER);
            } else if (streetNumberItems.length > 0 && e.key === "Enter") {
                setStreetNumberFormValue(streetNumberItems[activeListItemIndex]);
            }
        },
        [
            closeAllLists,
            onChangeInputStreetNumber,
            numbersOfSelectedStreet,
            activeListItemIndex,
            handleKeyArrowDown,
            handleKeyArrowUp,
            streetNumberItems,
            setStreetNumberFormValue
        ]
    );
    const onChangeInputFloor = useCallback(
        (e: any) => {
            const floor = e.currentTarget.value;
            setValue("floor", floor);
        },
        [setValue]
    );

    const loadingSpinner = <UI.RotatingSpinner noLogo size={20} className="loading-items-spinner" />;

    const zipSelectionList = (
        <ul
            id="zip-dropdown-list"
            className={`${searchType === SEARCH_STATE.ZIP ? "searching" : ""} dropdown-list zip`}
        >
            {searchType === SEARCH_STATE.ZIP ? loadingSpinner : undefined}
            {zipItems && zipItems.length > 0
                ? zipItems.map((item, index) => (
                      <li key={index}>
                          <div className="list-item" id={`zip-list-item-${index}`} onClick={onClickZipItem}>
                              {item}
                          </div>
                      </li>
                  ))
                : undefined}
        </ul>
    );

    const citySelectionList = (
        <ul
            id="city-dropdown-list"
            className={`${searchType === SEARCH_STATE.CITY ? "searching" : ""} dropdown-list city `}
        >
            {searchType === SEARCH_STATE.CITY ? loadingSpinner : undefined}
            {nameItems && nameItems.length > 0
                ? nameItems.map((item, index) => (
                      <li key={index}>
                          <div className="list-item" id={`city-list-item-${index}`} onClick={onClickNameItem}>
                              {item}
                          </div>
                      </li>
                  ))
                : undefined}
        </ul>
    );

    const streetSelectionList = (
        <ul
            id="street-dropdown-list"
            className={`${searchType === SEARCH_STATE.STREET ? "searching" : ""} dropdown-list street`}
        >
            {searchType === SEARCH_STATE.STREET ? loadingSpinner : undefined}
            {streetItems && streetItems.length > 0
                ? streetItems.map((item, index) => (
                      <li key={index}>
                          <div
                              className="list-item"
                              id={`street-list-item-${index}`}
                              data-streetid={item.id}
                              onClick={onClickStreetItem}
                          >
                              {item.street}
                          </div>
                      </li>
                  ))
                : undefined}
        </ul>
    );

    const streetNumberSelectionList = (
        <ul
            id="streetnumber-dropdown-list"
            className={`${searchType === SEARCH_STATE.STREETNUMBER ? "searching" : ""} dropdown-list number`}
        >
            {searchType === SEARCH_STATE.STREETNUMBER ? loadingSpinner : undefined}
            {streetNumberItems && streetNumberItems.length > 0
                ? streetNumberItems.map((number, index) => (
                      <li key={number}>
                          <div
                              className="list-item"
                              id={`streetnumber-list-item-${index}`}
                              onClick={onClickStreetNumberItem}
                          >
                              {number}
                          </div>
                      </li>
                  ))
                : undefined}
        </ul>
    );

    return (
        <Step>
            <UI.Container className="pt-4">
                <UI.Row>
                    <UI.Col md={uiStore.defaultColumnSize.md}>
                        <UI.Form onSubmit={handleSubmit(onSubmit)} autoComplete="nope">
                            <UI.Row>
                                <UI.Col xs={4} sm={4}>
                                    <UI.Input
                                        id="zip"
                                        label={`${t("screens.realestate.zip")} *`}
                                        {...register("zip")}
                                        type="text"
                                        onChange={onChangeInputCityZip}
                                        onKeyDown={onKeyDownInputCityZip}
                                        onClick={onClickInputCityZip}
                                        onFocus={onFocusInput}
                                        autoComplete="nope"
                                        errorMsg={errors.zip ? errors.zip.message : undefined}
                                    />
                                    {zipSelectionList}
                                </UI.Col>
                                <UI.Col xs={8} sm={8}>
                                    <UI.Input
                                        id="city"
                                        label={`${t("screens.realestate.city")} *`}
                                        {...register("city")}
                                        type="text"
                                        onChange={onChangeInputCityName}
                                        onFocus={onFocusInput}
                                        onKeyDown={onKeyDownInputCityName}
                                        onClick={onClickInputCityName}
                                        autoComplete="nope"
                                        errorMsg={errors.city ? errors.city.message : undefined}
                                    />
                                    {citySelectionList}
                                </UI.Col>
                            </UI.Row>
                            {error === ERROR_TYPE.ZIP ? (
                                <p className="alert text-danger">{t("screens.realestate.error.zip")}</p>
                            ) : undefined}
                            {error === ERROR_TYPE.CITY ? (
                                <p className="alert text-danger">{t("screens.realestate.error.city")}</p>
                            ) : undefined}
                            <UI.Row>
                                <UI.Col sm={12} md={6}>
                                    <UI.Input
                                        id="street"
                                        label={`${t("screens.realestate.street")} *`}
                                        {...register("street")}
                                        type="text"
                                        onChange={onChangeInputStreet}
                                        onFocus={onFocusInput}
                                        onKeyDown={onKeyDownInputStreet}
                                        onClick={onClickInputStreet}
                                        autoComplete="nope"
                                        readOnly={!(zipWasSelected && cityWasSelected)}
                                        errorMsg={errors.street ? errors.street.message : undefined}
                                    />
                                    {streetSelectionList}
                                </UI.Col>
                                <UI.Col sm={6} md={3}>
                                    <UI.Input
                                        id="number"
                                        label={`${t("screens.realestate.streetnumber")} *`}
                                        {...register("number")}
                                        type="text"
                                        onChange={onChangeInputStreetNumber}
                                        onFocus={onFocusInput}
                                        onClick={onClickInputStreetNumber}
                                        autoComplete="off"
                                        onKeyDown={onKeyDownInputStreetNumber}
                                        readOnly={!streetWasSelected}
                                        errorMsg={errors.number ? errors.number.message : undefined}
                                    />
                                    {streetNumberSelectionList}
                                    <input type="hidden" />
                                </UI.Col>
                                <UI.Col sm={6} md={3}>
                                    <UI.Input
                                        id="floor"
                                        label={t("screens.realestate.floor")}
                                        {...register("floor")}
                                        type="text"
                                        onChange={onChangeInputFloor}
                                        onFocus={onFocusInput}
                                        readOnly={!streetWasSelected}
                                        autoComplete="nope"
                                    />
                                </UI.Col>
                            </UI.Row>
                            {error === ERROR_TYPE.STREET ? (
                                <p className="alert text-danger">{t("screens.realestate.error.street")}</p>
                            ) : undefined}
                            {error === ERROR_TYPE.STREETNUMBER ? (
                                <p className="alert text-danger">{t("screens.realestate.error.streetnumber")}</p>
                            ) : undefined}
                            <UI.Button
                                disabled={!(streetWasSelected && streetNumberWasSelected)}
                                onClick={handleSubmit(onSubmit)}
                                label={t("labels.continue")}
                                id="realestate-submit-button"
                            />
                        </UI.Form>
                    </UI.Col>
                </UI.Row>
            </UI.Container>
        </Step>
    );
};

export const Realestate = Sentry.withProfiler(observer(RealestateBase));
